import {
  ActivityCategory,
  ActivityDifficulty,
  ContentType,
  CreateOrUpdateActivityContentInput,
  CreateOrUpdateActivityInput,
} from "@/graphql/graphql";

import type {
  ActivityBlock,
  ActivityFormValues,
  ActivitySubmitAction,
  DraftContent,
  DraftQuestionContent,
} from "./ActivityCreatePage.types";

export const responseTimeOptions = [
  { key: "SHORT", label: "SHORT (e.g. single word answers) (10s)", min: 2, max: 10 },
  { key: "MEDIUM", label: "MEDIUM (e.g. categorization) (30s)", min: 5, max: 30 },
  { key: "LONG", label: "LONG (45s)", min: 10, max: 45 },
] as const;

export const RESPONSE_TIME_STEP_SEC = 5;
export const SMALLEST_RESPONSE_TIME_SEC = 5;
export const LONGEST_RESPONSE_TIME_SEC = 600;

// Default response time is the maximum of the MEDIUM preset.
const DEFAULT_CUSTOM_RESPONSE_TIME_SEC = (
  responseTimeOptions.find((o) => o.key === "MEDIUM") as { max: number }
).max;
// Longest minimum response time is the maximum of the SHORT preset.
const LONGEST_MINIMUM_RESPONSE_TIME_SEC = (
  responseTimeOptions.find((o) => o.key === "SHORT") as { max: number }
).max;
const DEFAULT_MAX_SILENCE_SEC = 7;

export const calcCustomResponseMinimumSec = (maxSec: number): number => {
  if (maxSec < SMALLEST_RESPONSE_TIME_SEC) {
    return maxSec;
  }

  const potentialMin = Math.round(0.2 * maxSec);
  if (potentialMin > LONGEST_MINIMUM_RESPONSE_TIME_SEC) {
    return LONGEST_MINIMUM_RESPONSE_TIME_SEC;
  }

  return potentialMin;
};

/**
 * Clamps the custom response time max seconds to this range:
 *
 *   `CUSTOM_SMALLEST_RESPONSE_TIME_SEC <= maxSec <= CUSTOM_LARGEST_RESPONSE_TIME_SEC`
 *
 * */
export const clampCustomResponseTimeMaxSec = (maxSec: number): number => {
  if (maxSec < SMALLEST_RESPONSE_TIME_SEC) {
    return SMALLEST_RESPONSE_TIME_SEC;
  } else if (maxSec > LONGEST_RESPONSE_TIME_SEC) {
    return LONGEST_RESPONSE_TIME_SEC;
  }

  return maxSec;
};

export const calcSilenceBeforeResponse = (minSec: number, maxSec: number): number =>
  Math.min(Math.max(DEFAULT_MAX_SILENCE_SEC, minSec), maxSec);

export const resolveResponseTimings = ({
  responsePreset,
  customDurationMax,
  customExactTime,
}: Pick<ActivityBlock, "responsePreset" | "customDurationMax" | "customExactTime">) => {
  if (responsePreset !== "CUSTOM") {
    const { min: responseMinimumDurationSec, max: responseTimeSeconds } = responseTimeOptions.find(
      (o) => o.key === responsePreset
    ) as {
      min: number;
      max: number;
    };
    const silenceBeforeResponseSec = calcSilenceBeforeResponse(
      responseMinimumDurationSec,
      responseTimeSeconds
    );

    return {
      responseMinimumDurationSec,
      responseTimeSeconds,
      silenceBeforeResponseSec,
    };
  }

  if (customExactTime) {
    return {
      responseMinimumDurationSec: customDurationMax,
      responseTimeSeconds: customDurationMax,
      silenceBeforeResponseSec: customDurationMax,
    };
  }

  const responseTimeSeconds = clampCustomResponseTimeMaxSec(customDurationMax);
  const responseMinimumDurationSec = calcCustomResponseMinimumSec(responseTimeSeconds);
  const silenceBeforeResponseSec = calcSilenceBeforeResponse(
    responseMinimumDurationSec,
    responseTimeSeconds
  );

  return {
    responseMinimumDurationSec,
    responseTimeSeconds,
    silenceBeforeResponseSec,
  };
};

export const resolveDraftDuration = (
  content: Pick<ActivityBlock, "responsePreset" | "customDurationMax" | "customExactTime">
) => {
  if (content.responsePreset !== "CUSTOM") {
    return content.responsePreset;
  }

  return resolveResponseTimings(content);
};

/** Draft JSON may use form field `questionType` or published-activity shape `questionTypeKey`. */
export const resolveDraftQuestionType = (content: {
  questionType?: string | null;
  questionTypeKey?: string | null;
}): string => {
  const questionType = content.questionType?.trim();
  if (questionType) {
    return questionType;
  }

  return content.questionTypeKey?.trim() ?? "";
};

const draftQuestionBlockToFormBlock = (content: DraftQuestionContent): ActivityBlock => {
  const block: ActivityBlock = {
    ...emptyBlock(ContentType.Question),
    activityContentId: content.activityContentId,
    text: typeof content.text === "string" ? content.text : "",
    questionType: resolveDraftQuestionType(content),
    isScorable: !!content.isScorable,
    scoringNotes: typeof content.scoringNotes === "string" ? content.scoringNotes : "",
    expectedAnswers: content.isScorable
      ? Array.isArray(content.expectedAnswers)
        ? content.expectedAnswers.map(String)
        : [""]
      : null,
  };

  const { duration, responseTimeSeconds } = content;

  if (typeof duration === "string" && ["SHORT", "MEDIUM", "LONG"].includes(duration)) {
    return {
      ...block,
      responsePreset: duration as "SHORT" | "MEDIUM" | "LONG",
    };
  }

  if (duration && typeof duration === "object" && "responseTimeSeconds" in duration) {
    const max = duration.responseTimeSeconds ?? DEFAULT_CUSTOM_RESPONSE_TIME_SEC;
    const min = duration.responseMinimumDurationSec;
    const clampedMax = clampCustomResponseTimeMaxSec(max);
    return {
      ...block,
      responsePreset: "CUSTOM",
      customDurationMax: clampedMax,
      customExactTime: min != null && min === clampedMax,
    };
  }

  return {
    ...block,
    responsePreset: getResponsePresetFromSeconds(responseTimeSeconds),
  };
};

const isQuestionDraftContent = (content: DraftContent): content is DraftQuestionContent =>
  content.contentType === ContentType.Question;

/** Maps a form QUESTION block to the draft JSON wire shape. */
export const formQuestionBlockToDraftContent = (content: ActivityBlock) => ({
  activityContentId: content.activityContentId,
  contentType: content.contentType,
  text: content.text,
  isScorable: content.isScorable,
  scoringNotes: content.scoringNotes,
  questionType: resolveDraftQuestionType(content),
  duration: resolveDraftDuration(content),
  expectedAnswers: content.isScorable ? content.expectedAnswers : null,
});

/** Full reset shape for useFieldArray and controlled fields after save / load. */
export const normalizeActivityFormForReset = (next: ActivityFormValues): ActivityFormValues => ({
  ...next,
  contents: (next.contents ?? []).map((content) => ({
    ...emptyBlock(content.contentType),
    ...content,
  })),
});

export const emptyBlock = (contentType: ActivityBlock["contentType"]): ActivityBlock => ({
  activityContentId: undefined,
  contentType,
  text: "",
  questionType: "",
  responsePreset: "MEDIUM",
  customDurationMax: DEFAULT_CUSTOM_RESPONSE_TIME_SEC,
  customExactTime: false,
  isScorable: false,
  scoringNotes: "",
  expectedAnswers: contentType === ContentType.Question ? [""] : null,
});

/** Drop `voiceMessageId` when it is not in the linkable list returned by the API. */
export const withoutInvalidVoiceMessageId = (
  data: ActivityFormValues,
  linkableVoiceMessageIds: number[]
): ActivityFormValues => {
  const voiceMessageId = data.voiceMessageId;
  if (voiceMessageId == null || linkableVoiceMessageIds.includes(voiceMessageId)) {
    return data;
  }

  return { ...data, voiceMessageId: null };
};

/** Default empty form values for a new activity draft. */
export const emptyActivityFormValues = (): ActivityFormValues => ({
  title: "",
  description: "",
  specialistId: null,
  category: null,
  difficulty: null,
  activityTypeKeys: [],
  voiceMessageId: null,
  contents: [],
});

/**
 * Maps the form to `CreateActivityInput`. Call only after publish validation (category, difficulty,
 * and question fields are non-null / complete).
 */
export const buildCreateActivityInput = (data: ActivityFormValues): CreateOrUpdateActivityInput => {
  if (data.specialistId == null) {
    throw new Error("Specialist is required to publish activities.");
  }
  if (!data.category || !data.difficulty) {
    throw new Error("Category and difficulty are required to publish activities.");
  }

  const activityTypeKeys =
    data.category === ActivityCategory.CognitiveExercise ? data.activityTypeKeys ?? [] : undefined;

  const contents: CreateOrUpdateActivityContentInput[] = data.contents.map((content) => {
    if (content.contentType !== ContentType.Question) {
      return {
        activityContentId: content.activityContentId,
        contentType: ContentType.Instruction,
        text: content.text,
      };
    }

    const { responseMinimumDurationSec, responseTimeSeconds, silenceBeforeResponseSec } =
      resolveResponseTimings(content);

    const out: CreateOrUpdateActivityContentInput = {
      activityContentId: content.activityContentId,
      contentType: ContentType.Question,
      text: content.text,
      questionType: content.questionType,
      responseTimeSeconds,
      responseMinimumDurationSec,
      silenceBeforeResponseSec,
      isScorable: content.isScorable,
      scoringNotes: content.scoringNotes,
    };

    if (content.isScorable) {
      const answers = (content.expectedAnswers ?? [])
        .map((a) => String(a).trim())
        .filter((a) => a.length > 0);
      if (answers.length > 0) {
        out.expectedAnswers = answers;
      }
    }

    return out;
  });

  return {
    id: data.activityId,
    specialistId: data.specialistId,
    title: data.title.trim(),
    description: data.description?.trim() ?? "",
    category: data.category,
    difficulty: data.difficulty,
    activityTypeKeys,
    contents,
    ...(data.voiceMessageId != null ? { voiceMessageId: data.voiceMessageId } : {}),
  };
};

export const buildDraftActivityJSON = (data: ActivityFormValues) => {
  const draftActivityJSON = { ...data };
  if (data.specialistId != null) {
    draftActivityJSON.specialistId = data.specialistId;
  }
  // Only send activity type keys to backend for cognitive exercises, as it's not used for other
  // categories. Useful to keep the activity types while editing, in case the user changes the
  // category while editing, and doesn't want to lose the activity type they've selected. When
  // other categories are selected, the activity types section is hidden.
  if (data.category !== ActivityCategory.CognitiveExercise) {
    delete draftActivityJSON.activityTypeKeys;
  }

  return {
    ...draftActivityJSON,
    contents: draftActivityJSON.contents?.map((content) => {
      if (content.contentType !== ContentType.Question) {
        return {
          activityContentId: content.activityContentId,
          contentType: content.contentType,
          text: content.text,
        };
      }

      return formQuestionBlockToDraftContent(content);
    }),
  };
};

/** Backward-compat: map old draft `responseTimeSeconds` to a preset key (using max as anchor). */
const getResponsePresetFromSeconds = (seconds: number | undefined): "SHORT" | "MEDIUM" | "LONG" => {
  if (!seconds) {
    return "MEDIUM";
  }
  const found = responseTimeOptions.find((o) => o.max === seconds);
  if (found) {
    return found.key;
  }
  if (seconds <= responseTimeOptions[0].max) {
    return responseTimeOptions[0].key;
  }
  if (seconds >= responseTimeOptions[2].max) {
    return responseTimeOptions[2].key;
  }

  return responseTimeOptions[1].key;
};

export const buildFormValuesFromDraftActivityJSON = (activityJSON: unknown): ActivityFormValues => {
  if (!activityJSON || typeof activityJSON !== "object") {
    return emptyActivityFormValues();
  }

  const specialistId = activityJSON["specialistId"] ?? null;
  const voiceMessageId = activityJSON["voiceMessageId"];
  const parsedActivityContents = (activityJSON["contents"] ?? []) as Array<DraftContent>;

  return {
    ...activityJSON,
    specialistId,
    voiceMessageId,
    activityId: activityJSON["id"],
    contents: parsedActivityContents.map((content): ActivityBlock => {
      if (!isQuestionDraftContent(content)) {
        return {
          ...emptyBlock(ContentType.Instruction),
          activityContentId: content.activityContentId,
          text: typeof content.text === "string" ? content.text : "",
        };
      }

      return draftQuestionBlockToFormBlock(content);
    }),
  } as ActivityFormValues;
};

export const validateSpecialistId = (
  specialistId: number | null,
  submitAction: ActivitySubmitAction
) => {
  if (specialistId != null) {
    return true;
  }

  return submitAction === "draft"
    ? "Specialist is required to save draft."
    : "Specialist is required to publish.";
};

export const validateTitle = (value: string | undefined, submitAction: ActivitySubmitAction) => {
  if (value?.trim()) {
    return true;
  }

  return submitAction === "draft"
    ? "Title is required to save draft."
    : "Title is required to publish.";
};

export const validateCategory = (
  category: ActivityFormValues["category"],
  submitAction: ActivitySubmitAction
) => {
  if (submitAction !== "publish" || category) {
    return true;
  }

  return "Category is required to publish.";
};

export const validateDifficulty = (
  difficulty: ActivityDifficulty | null,
  submitAction: ActivitySubmitAction
) => {
  if (submitAction !== "publish" || difficulty) {
    return true;
  }

  return "Difficulty is required to publish.";
};

export const validateActivityTypeKeys = (
  category: ActivityFormValues["category"],
  activityTypeKeys: ActivityFormValues["activityTypeKeys"] | undefined,
  submitAction: ActivitySubmitAction
) => {
  if (
    submitAction !== "publish" ||
    category !== ActivityCategory.CognitiveExercise ||
    activityTypeKeys?.length
  ) {
    return true;
  }

  return "Select at least one activity type to publish.";
};

export const validateContents = (
  contents: ActivityFormValues["contents"],
  submitAction: ActivitySubmitAction
) => {
  if (submitAction !== "publish" || contents?.length) {
    return true;
  }

  return "Add at least one content block to publish.";
};

export const validateContentText = (
  value: string | undefined,
  submitAction: ActivitySubmitAction
) => {
  if (submitAction !== "publish" || value?.trim()) {
    return true;
  }

  return "Content text is required to publish.";
};

export const validateQuestionType = (
  value: string | undefined,
  submitAction: ActivitySubmitAction
) => {
  if (submitAction !== "publish" || value?.trim()) {
    return true;
  }

  return "Question type is required to publish.";
};

export const validateExpectedAnswers = (
  answers: string[] | undefined,
  isScorable: boolean,
  submitAction: ActivitySubmitAction
) => {
  if (submitAction !== "publish" || !isScorable) {
    return true;
  }
  const hasNonEmpty = (answers ?? []).some((answer) => String(answer).trim().length > 0);

  return hasNonEmpty ? true : "Add at least one expected answer when scoring is enabled.";
};
